import { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", key: "home" },
  { to: "/solutions", label: "Solutions", key: "solutions" },
  { to: "/services", label: "Services", key: "services" },
  { to: "/industries", label: "Industries", key: "industries" },
];

const COMPANY_LINKS = [
  { to: "/about", label: "About us", key: "about" },
  { to: "/approach", label: "Approach", key: "approach" },
  { to: "/careers", label: "Careers", key: "careers" },
  { to: "/brochure", label: "Brochure", key: "brochure" },
  { to: "/contact", label: "Contact us", key: "contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const companyRef = useRef(null);
  const closeTimer = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setCompanyOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (companyRef.current && !companyRef.current.contains(e.target)) {
        setCompanyOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const openCompany = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setCompanyOpen(true);
  };
  const scheduleCloseCompany = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setCompanyOpen(false), 150);
  };

  const isCompanyActive = COMPANY_LINKS.some((l) => loc.pathname === l.to);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-gray-200"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="h-20 flex items-center">
          <Link to="/" data-testid="nav-logo" aria-label="Sunsri Software — Home" className="flex items-center gap-3 group">
            <img
              src="/brand/sunsri-logo.png"
              alt="Sunsri Software Private Limited"
              className="h-14 w-auto select-none"
              draggable={false}
            />
            <div className="leading-tight font-sans">
              <div className="text-[15px] font-semibold tracking-[0.18em] text-[#111827] uppercase">Sunsri Software</div>
              <div className="text-[10px] font-medium tracking-[0.32em] text-gray-500 uppercase mt-1">Private Limited</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-9 ml-auto mr-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `link-underline text-sm font-medium transition-colors ${
                    isActive ? "text-[#111827]" : "text-gray-600 hover:text-[#111827]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <div
              ref={companyRef}
              className="relative"
              onMouseEnter={openCompany}
              onMouseLeave={scheduleCloseCompany}
            >
              <button
                type="button"
                data-testid="nav-link-company"
                aria-haspopup="menu"
                aria-expanded={companyOpen}
                onClick={() => setCompanyOpen((v) => !v)}
                className={`link-underline inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                  isCompanyActive ? "text-[#111827]" : "text-gray-600 hover:text-[#111827]"
                }`}
              >
                Company <ChevronDown size={14} className={`transition-transform ${companyOpen ? "rotate-180" : ""}`} />
              </button>
              {companyOpen && (
                <div
                  data-testid="company-dropdown"
                  role="menu"
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-3 min-w-[200px] bg-white border border-gray-200 shadow-sm py-2"
                  onMouseEnter={openCompany}
                  onMouseLeave={scheduleCloseCompany}
                >
                  {COMPANY_LINKS.map((l) => (
                    <NavLink
                      key={l.key}
                      to={l.to}
                      data-testid={`nav-company-${l.key}`}
                      className={({ isActive }) =>
                        `block px-5 py-2.5 text-sm transition-colors ${
                          isActive ? "text-[#111827] bg-gray-50" : "text-gray-600 hover:text-[#111827] hover:bg-gray-50"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" data-testid="nav-contact-button" className="btn-gold">
              Get in touch <ArrowUpRight size={16} />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 -mr-2 ml-auto text-[#111827]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.key}`}
                className={({ isActive }) =>
                  `font-serif text-2xl ${isActive ? "text-[#111827]" : "text-gray-600"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="overline mt-2">Company</div>
            {COMPANY_LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={`mobile-nav-company-${l.key}`}
                className={({ isActive }) =>
                  `font-serif text-2xl ${isActive ? "text-[#111827]" : "text-gray-600"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-nav-contact-button" className="btn-gold mt-2 w-fit">
              Get in touch <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
